/**
 * **Your servers** — the table of servers this device knows, as the Local page
 * paints it (plan `docs/plans/LOCAL-HTTP-AND-CLOUD-ADOPTION.md` §2/C5, card
 * `t_3c5c353b`).
 *
 * The page has two sections and this module is the seam between them:
 *
 *   - **Your servers** — `buildLocalServerRows`: one row per server this device
 *     holds a record of, which is the buffered profile (`services/serverProfiles.ts`,
 *     plan §2/C2 — the account's list with this device's own facts) merged with
 *     the device's saved connections (`services/savedConnections.ts` — a hub it
 *     reached by address, including one whose address a person typed). One server
 *     is one row: a record that appears in both is folded, never painted twice.
 *     A server **removed on this device** (`facts.forgotten`,
 *     `services/forgottenServers.ts`) is not a row at all — the owner's `Forget`
 *     removes the row for every source, restart included, and the account's own
 *     list is not touched (plan `LOCAL-SERVER-ONBOARDING-JOURNEY.md` §3/C4, §7 Q3).
 *   - **Found on this network** — `hubsFoundOnNetwork`: the scan's records,
 *     minus the ones a row already represents (the same hub must not be offered
 *     twice, once as "found" and once as "known").
 *
 * ── Why it is its own module ────────────────────────────────────────────────
 *
 * Every rule here is a decision about data the page then paints — which address
 * a row will dial, what its route is, whether the network answered for it. Those
 * are exactly the kind of rule the family proves with a unit test rather than
 * with a screenshot of a view, and the view has no DOM to bring into them: the
 * inputs are plain records (`ServerProfile`, `SavedConnection`, `DiscoveredHub`).
 *
 * ── The three-valued availability ───────────────────────────────────────────
 *
 * `online` — the scan found this server (by its own id, or at the address the row
 * holds). `offline` — the LAN was asked on this surface and did not answer for
 * it (which is the honest reading for a hub on another subnet too: *not seen on
 * this network*). `unknown` — nobody asked (this surface cannot scan, or has not
 * scanned yet). A badge that cannot say "nobody asked" turns a page load into a
 * claim about the network.
 */
import { HUB_FRONT_DOOR_PORT } from '../config/runtime';
import { parseHubAddressInput } from '../utils/hubAddress';
import type { DiscoveredHub } from './discovery';
import { isServerForgotten, type ForgottenServer } from './forgottenServers';
import type { SavedConnection } from './savedConnections';
import { hydrateServerProfiles, profileFor, type ServerProfile, type ServerProfileLicence } from './serverProfiles';

/** What the LAN last said about a server. See the module docblock. */
export type ServerAvailability = 'online' | 'offline' | 'unknown';

/** The door a row will be opened through. */
export type ServerRoute = 'local' | 'cloud';

/** Why that door — which setting or fact decided it. */
export type ServerRouteSource = 'cloud-only' | 'manual' | 'last-route' | 'automatic';

/** Where a row's address came from. */
export type ServerAddressSource = 'last-route' | 'manual' | 'lan' | 'saved';

/** One server of the device, as a table row. */
export interface LocalServerRow {
  /** Stable across renders: the cloud row's id, the hub's id, or the saved row's id. */
  key: string;
  name: string;
  cloudId: string | null;
  hubId: string | null;
  /** The account's public id for the hub, when the buffer carries one. */
  publicId: string | null;
  /** The device's own saved-connection row (`Forget` needs it), or null. */
  savedId: string | null;
  /** The address this row would dial on the LAN, or null when it has none. */
  ip: string | null;
  port: number | null;
  secure?: boolean;
  /** What the table prints — the dialled address, or `''` when there is none. */
  address: string;
  addressSource: ServerAddressSource | null;
  /** Licence facts, when the cloud reported them (plan §2/C3). */
  licence: ServerProfileLicence | null;
  /** The door this server will be opened through. */
  route: ServerRoute;
  routeSource: ServerRouteSource;
  availability: ServerAvailability;
  /** The scan record this row is on the network as, or null. */
  found: DiscoveredHub | null;
  /** A saved thumbnail/icon for the row, as stored (the view builds the URL). */
  image: string | null;
}

export interface LocalServerTableFacts {
  /** The device's buffer (`services/serverProfiles.ts`), as this session holds it. */
  profiles: ServerProfile[];
  /** The device's saved connections. */
  saved: SavedConnection[];
  /** The last scan's records. */
  discovered: DiscoveredHub[];
  /** The LAN has been asked at least once on this surface. */
  scanned: boolean;
  /**
   * The servers **removed on this device** (`services/forgottenServers.ts`), as
   * this session holds them — the one filter behind the owner's decision that
   * `Forget` *removes the row* (plan `LOCAL-SERVER-ONBOARDING-JOURNEY.md` §3/C4,
   * owner decision §7 Q3, card `t_36033b60`).
   *
   * It lives here, in the table, so every shape and every consumer agrees: the
   * desktop row and the phone card are the same rows, and a buffered profile the
   * account still lists cannot paint itself back after a removal. An absent list
   * is "nothing was removed on this device" — the honest reading for a caller
   * with no device storage (a test, another surface's own table).
   */
  forgotten?: ForgottenServer[];
}

/** An address candidate: a host and the port it answers on. */
export interface AddressCandidate {
  host: string;
  port: number;
  secure?: boolean;
  source: ServerAddressSource;
}

function sameHost(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** The port of a stored address: the front door when nothing was written. */
function portOf(value: unknown): number {
  const parsed = typeof value === 'string' ? Number(value) : value;
  if (typeof parsed !== 'number' || !Number.isFinite(parsed) || parsed <= 0 || parsed > 65535) {
    return HUB_FRONT_DOOR_PORT;
  }
  return Math.trunc(parsed);
}

/** `host`, or `host:port` when the port is not the front door (card `t_c2446418`). */
export function formatServerAddress(host: string, port: number): string {
  return port === HUB_FRONT_DOOR_PORT ? host : `${host}:${port}`;
}

/**
 * The address this device knows for one hub, by either spelling of its id — the
 * first candidate of the hub's own record (`profileAddresses`, read through
 * `services/serverProfiles.ts`'s lookup, hydrated first: a connect can be the
 * first thing that happens after a launch).
 *
 * `null` when there is no record or no address in it: **"no address" is not an
 * address**, and a surface that names one nobody measured points a person at a
 * hub that is not there. It is the same answer for the two callers that need it —
 * the router's hub-restore when a connect is refused, and the launcher's search
 * hit (card `t_dbfe328d`) — so one failure is never worded with one address on
 * one surface and another elsewhere.
 */
export function knownHubAddress(ref: {
  cloudId?: string | null;
  hubId?: string | null;
}): { ip: string; port: number } | null {
  const rows = hydrateServerProfiles();
  const profile = profileFor({ cloudId: ref.cloudId ?? null, hubId: ref.hubId ?? null }, rows);
  const first = profile ? (profileAddresses(profile)[0] ?? null) : null;
  return first ? { ip: first.host, port: first.port } : null;
}

/** A profile worth a row: the cloud lists it, or this device has facts about it. */
function profileIsRow(row: ServerProfile): boolean {
  if (row.cloudListed) return true;
  return (
    row.lanAddresses.length > 0 || !!row.manualAddress || row.manual || row.cloudOnly || !!row.lastRoute
  );
}

function savedIsRow(row: SavedConnection): boolean {
  return !row.isCloud && typeof row.ip === 'string' && row.ip.trim().length > 0;
}

/** The profile's ids, lowercased, the way the buffer keys them. */
function profileHubId(row: ServerProfile | null | undefined): string | null {
  return row?.hubId?.trim().toLowerCase() ?? null;
}

function savedHubId(row: SavedConnection | null | undefined): string | null {
  return row?.hubId?.trim().toLowerCase() ?? null;
}

/**
 * The address candidates of one record, in the order this device knows them:
 * the address the settings pin (manual), the address that last opened the hub,
 * then the addresses the LAN answered at.
 *
 * Exported because it is the one reading of "which address does this device
 * know for this hub": the table dials the first one (`rowDial`), and the
 * router's hub-restore hands it to the hub-open plan when a connect is refused
 * (`utils/hubOpenFallback.ts` → `address`, card `t_dbfe328d`) — so a sentence
 * about a hub's LAN door is never written with an address nobody measured.
 */
export function profileAddresses(row: ServerProfile): AddressCandidate[] {
  const out: AddressCandidate[] = [];
  const manual = parseHubAddressInput(row.manualAddress);
  if (row.manual && manual) {
    out.push({ host: manual.host, port: manual.port, source: 'manual' });
  }
  if (row.lastRoute?.host) {
    out.push({
      host: row.lastRoute.host,
      port: row.lastRoute.port ?? HUB_FRONT_DOOR_PORT,
      secure: row.lastRoute.secure,
      source: 'last-route',
    });
  }
  for (const address of row.lanAddresses) {
    out.push({ host: address.host, port: address.port, secure: address.secure, source: 'lan' });
  }
  return out;
}

function savedAddress(row: SavedConnection): AddressCandidate {
  return {
    host: (row.ip ?? '').trim(),
    port: portOf(row.port),
    secure: row.secure,
    source: 'saved',
  };
}

/** Does a saved connection carry the same hub as this profile? */
function savedMatchesProfile(profile: ServerProfile, saved: SavedConnection): boolean {
  const profileId = profileHubId(profile);
  const savedId = savedHubId(saved);
  if (profileId && savedId && profileId === savedId) return true;
  // The same address is the same server, even before either record learned the
  // hub's id (the app backfills ids from discovery, measured live).
  const savedAddress$ = savedAddress(saved);
  if (!savedAddress$.host) return false;
  return profileAddresses(profile).some(
    (candidate) => sameHost(candidate.host, savedAddress$.host) && candidate.port === savedAddress$.port,
  );
}

/** Does the scan's record describe this row's server? */
export function rowMatchesHub(row: LocalServerRow, hub: DiscoveredHub): boolean {
  const rowId = row.hubId?.trim().toLowerCase();
  const hubId = hub.hubId?.trim().toLowerCase();
  if (rowId && hubId && rowId === hubId) return true;
  if (!hub.ip) return false;
  const hubPort = portOf(hub.port);
  if (row.ip && sameHost(row.ip, hub.ip) && portOf(row.port) === hubPort) return true;
  // The record's own address list counts too: a hub that answered at a second
  // address is still the server this row is about (card `t_9cdad513`).
  for (const address of hub.addresses ?? []) {
    if (row.ip && sameHost(row.ip, address) && portOf(row.port) === hubPort) return true;
  }
  return false;
}

/**
 * The table: one row per server this device knows, buffered profiles merged with
 * the saved connections that describe the same hub — and the servers removed on
 * this device (`facts.forgotten`) left out of it (plan
 * `LOCAL-SERVER-ONBOARDING-JOURNEY.md` §3/C4).
 */
export function buildLocalServerRows(facts: LocalServerTableFacts): LocalServerRow[] {
  const rows: LocalServerRow[] = [];
  const consumedSaved = new Set<SavedConnection>();
  const scannedHubs = facts.discovered.filter((hub) => !!hub);
  const removed = facts.forgotten ?? [];

  for (const profile of facts.profiles) {
    if (!profileIsRow(profile)) continue;
    const saved =
      facts.saved.find((connection) => savedIsRow(connection) && savedMatchesProfile(profile, connection)) ?? null;
    // The saved connection is spent whether or not the row is painted: a removal
    // takes the *row*, and the two records it was folded from are one server —
    // leaving the connection unconsumed would paint the same server back as a
    // device-saved row on the very next render.
    if (saved) consumedSaved.add(saved);
    if (isServerForgotten({ cloudId: profile.cloudId, hubId: profile.hubId ?? savedHubId(saved) }, removed)) {
      continue;
    }

    const addresses = profileAddresses(profile);
    const primary = addresses[0] ?? (saved ? savedAddress(saved) : null);
    const hubId = profile.hubId ?? savedHubId(saved);
    const key = profile.cloudId ?? hubId ?? saved?.id ?? '';
    if (!key) continue;

    const row: LocalServerRow = {
      key,
      name: profile.name ?? saved?.name ?? (primary ? formatServerAddress(primary.host, primary.port) : ''),
      cloudId: profile.cloudId,
      hubId: profile.hubId ?? (saved?.hubId ?? null),
      publicId: profile.publicId,
      savedId: saved?.id ?? null,
      ip: primary?.host ?? null,
      port: primary ? primary.port : null,
      address: primary ? formatServerAddress(primary.host, primary.port) : '',
      addressSource: primary?.source ?? null,
      licence: profile.licence,
      route: 'local',
      routeSource: 'automatic',
      availability: 'unknown',
      found: null,
      image: saved?.image ?? saved?.icon ?? null,
    };
    if (typeof primary?.secure === 'boolean') row.secure = primary.secure;
    applyRoute(row, profile);
    rows.push(row);
  }

  for (const saved of facts.saved) {
    if (!savedIsRow(saved) || consumedSaved.has(saved)) continue;
    // A row this device saves is keyed by the hub it names (`savedHubId`), and a
    // removal names that hub: the row stays out (plan §3/C4).
    if (isServerForgotten({ hubId: savedHubId(saved) }, removed)) continue;
    const address = savedAddress(saved);
    rows.push({
      key: saved.hubId?.trim() || saved.id,
      name: saved.name || formatServerAddress(address.host, address.port),
      cloudId: null,
      hubId: saved.hubId?.trim() ?? null,
      publicId: null,
      savedId: saved.id,
      ip: address.host,
      port: address.port,
      secure: address.secure,
      address: formatServerAddress(address.host, address.port),
      addressSource: 'saved',
      licence: null,
      // A saved connection is an address a person reached: the LAN door is the
      // one it knows, and nothing in the record pins it to the tunnel.
      route: 'local',
      routeSource: 'automatic',
      availability: 'unknown',
      found: null,
      image: saved.image ?? saved.icon ?? null,
    });
  }

  for (const row of rows) {
    const found = scannedHubs.find((hub) => rowMatchesHub(row, hub)) ?? null;
    row.found = found;
    row.availability = found
      ? found.connectable === false
        ? 'offline'
        : 'online'
      : facts.scanned
        ? 'offline'
        : 'unknown';
  }

  return rows;
}

/** The route a row's own settings ask for — the same order `serverRoutePreference` reads. */
function applyRoute(row: LocalServerRow, profile: ServerProfile): void {
  if (profile.cloudOnly) {
    row.route = 'cloud';
    row.routeSource = 'cloud-only';
    return;
  }
  const manual = parseHubAddressInput(profile.manualAddress);
  if (profile.manual && manual) {
    row.route = 'local';
    row.routeSource = 'manual';
    return;
  }
  if (profile.lastRoute) {
    row.route = profile.lastRoute.route;
    row.routeSource = 'last-route';
    return;
  }
  // Nothing stored: today's automatic local-first route — the LAN address when
  // the row has one, the tunnel otherwise (plan §2/C4 item 3).
  row.route = row.ip ? 'local' : 'cloud';
  row.routeSource = 'automatic';
}

/**
 * The scan's records the table does **not** already hold: a server that is in
 * "Your servers" is not offered again as a find.
 *
 * A server removed on this device is deliberately offered again here: this
 * section follows the LAN, not the table, and the way back to a row a person
 * removed is exactly this list — opening the hub from here, or adding its address
 * with `+ Dodaj strežnik`, clears the removal
 * (`services/forgottenServers.ts` → `restoreForgottenServer`, plan §3/C4).
 */
export function hubsFoundOnNetwork(discovered: DiscoveredHub[], rows: LocalServerRow[]): DiscoveredHub[] {
  const out: DiscoveredHub[] = [];
  for (const hub of discovered) {
    if (!hub) continue;
    if (rows.some((row) => rowMatchesHub(row, hub))) continue;
    // Two records of one hub (a re-announce, an address change) are folded by
    // discovery already; a second fold here keeps a duplicate out of the section
    // even if a caller passes a list it did not dedupe.
    if (out.some((known) => hubMergeKeySafe(known) === hubMergeKeySafe(hub))) continue;
    out.push(hub);
  }
  return out;
}

/** The id a discovered record is one-and-the-same by (`ip:port` when it has no id). */
function hubMergeKeySafe(hub: DiscoveredHub): string {
  const id = hub.hubId?.trim().toLowerCase();
  if (id) return `id:${id}`;
  return `addr:${(hub.ip ?? '').trim().toLowerCase()}:${portOf(hub.port)}`;
}
